import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  fetchCategories, createCategory, updateCategory,
  deleteCategory, toggleCategoryStatus,
} from "../../../slice/category/categorySlice";

const BASE_URL = process.env.REACT_APP_API_URL;

const ListCategory = () => {
  const dispatch = useDispatch();
  const { categories, loading, error, pagination } = useSelector(
    (state) => state.category
  );

  const tableRef = useRef(null);
  const dtRef    = useRef(null);

  const [showModal,    setShowModal]    = useState(false);
  const [editItem,     setEditItem]     = useState(null);
  const [newName,      setNewName]      = useState("");
  const [categoryCode, setCategoryCode] = useState("");
  const [newImage,     setNewImage]     = useState(null);
  const [preview,      setPreview]      = useState(null);
  const [dragOver,     setDragOver]     = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [formError,    setFormError]    = useState("");
  const [searchTerm,   setSearchTerm]   = useState("");
  const [page,         setPage]         = useState(1);
  const limit = 10;

  useEffect(() => {
    dispatch(fetchCategories({ page, limit, search: searchTerm }));
  }, [dispatch, page, searchTerm]);

  useEffect(() => {
    if (!categories.length) return;
    const init = () => {
      if (window.$?.fn?.DataTable) {
        if (dtRef.current) { dtRef.current.destroy(); dtRef.current = null; }
        dtRef.current = window.$(tableRef.current).DataTable({
          responsive: true, pageLength: 10,
          columnDefs: [{ orderable: false, targets: [0, 1, 3, 4] }],
        });
      } else { setTimeout(init, 300); }
    };
    init();
    return () => { if (dtRef.current) { dtRef.current.destroy(); dtRef.current = null; } };
  }, [categories]);

  const handleImage = (file) => {
    if (file) { setNewImage(file); setPreview(URL.createObjectURL(file)); }
  };

  const openAddModal = () => {
    setEditItem(null); setNewName(""); setCategoryCode("");
    setPreview(null); setNewImage(null); setFormError(""); setShowModal(true);
  };

  const openEditModal = (cat) => {
    setEditItem(cat); setNewName(cat.name);
    setCategoryCode(cat.categoryCode || "");
    setPreview(cat.image ? `${BASE_URL}/uploads/categories/${cat.image}` : null);
    setNewImage(null); setFormError(""); setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false); setEditItem(null); setNewName(""); setCategoryCode("");
    setPreview(null); setNewImage(null); setFormError("");
  };

  const handleSave = async () => {
    if (!newName.trim()) { setFormError("Category name is required"); return; }
    setSaving(true); setFormError("");

    const formData = new FormData();
    formData.append("name", newName.trim());
    formData.append("categoryCode", categoryCode.trim());
    if (newImage) formData.append("image", newImage);

    try {
      if (editItem) {
        await dispatch(updateCategory({ id: editItem._id, formData })).unwrap();
        toast.success("Category updated successfully!");
      } else {
        await dispatch(createCategory(formData)).unwrap();
        setPage(1);
        toast.success("Category added successfully!");
      }
      dispatch(fetchCategories({ page: editItem ? page : 1, limit, search: searchTerm }));
      handleClose();
    } catch (err) {
      setFormError(err || "Something went wrong");
      toast.error(err || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await dispatch(deleteCategory(id)).unwrap();
      dispatch(fetchCategories({ page, limit, search: searchTerm }));
      toast.success("Category deleted successfully!");
    } catch (err) {
      toast.error(err || "Delete failed");
    }
  };

  const handleToggle = (id) => {
    dispatch(toggleCategoryStatus(id));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2,"0")}-${String(d.getMonth()+1).padStart(2,"0")}-${d.getFullYear()}`;
  };

  return (
    <div style={{ background: "#f6f7fb", minHeight: "100vh", padding: "28px 32px" }}>
      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, color: "#111827" }}>Category</h1>
          <p style={{ margin: "6px 0 0", color: "#9ca3af", fontSize: 14 }}>
            {pagination.total ?? categories.length} categories
          </p>
        </div>
        <button
          onClick={openAddModal}
          style={{
            padding: "12px 22px", borderRadius: 12, border: "none",
            background: "#111827", color: "#fff", fontWeight: 700,
            fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
          }}
        >
          + Add Category
        </button>
      </div>

      {/* ── Search ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{
          flex: 1, minWidth: 200, display: "flex", alignItems: "center", gap: 10,
          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "12px 16px",
        }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#9aa0ac" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            style={{ border: "none", outline: "none", flex: 1, fontSize: 14, background: "transparent" }}
          />
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #eef0f4", overflow: "hidden" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>Loading...</div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#dc2626" }}>{error}</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#fafbfc" }}>
                  {["Si No", "IMAGE", "NAME", "STATUS", "CREATED", "ACTIONS"].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left", padding: "14px 20px", fontSize: 11,
                        fontWeight: 700, letterSpacing: 0.5, color: "#9ca3af",
                        borderBottom: "1px solid #eef0f4", whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {categories.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: "50px 0", color: "#aaa" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <span style={{ fontSize: 14, fontWeight: 600, color: "#bbb" }}>No categories found</span>
                        <span style={{ fontSize: 12, color: "#d1d5db" }}>
                          {searchTerm ? `No results for "${searchTerm}"` : "Add your first category using the button above"}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  categories.map((cat, index) => (
                    <tr key={cat._id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td style={{ padding: "16px 20px" }}>
                        <div style={{
                          width: 38, height: 38, borderRadius: 10, background: "#f3f4f6",
                          overflow: "hidden", flexShrink: 0,
                        }}>
                          <img
                            src={cat.image ? `${BASE_URL}/uploads/categories/${cat.image}` : "/assets/images/default.png"}
                            alt={cat.name}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        </div>
                      </td>
                      <td style={{ padding: "16px 20px", fontSize: 14, fontWeight: 600, color: "#111827" }}>
                        {cat.name}
                      </td>
                      <td style={{ padding: "16px 20px" }}>
                        <label className="switch">
                          <input type="checkbox" checked={cat.status} onChange={() => handleToggle(cat._id)} />
                          <span className="slider round" />
                        </label>
                      </td>
                      <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>
                        {formatDate(cat.createdAt)}
                      </td>
                      <td style={{ padding: "16px 20px" }}>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            onClick={() => openEditModal(cat)}
                            style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#111827", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(cat._id)}
                            title="Delete"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: "#fff5f5", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                              <path d="M10 11v6" /><path d="M14 11v6" />
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "16px 20px" }}>
            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}
              style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: page === 1 ? "not-allowed" : "pointer" }}
            >Prev</button>
            {Array.from({ length: pagination.totalPages }, (_, i) => (
              <button key={i + 1} onClick={() => setPage(i + 1)}
                style={{
                  padding: "7px 14px", borderRadius: 8,
                  border: page === i + 1 ? "none" : "1px solid #e5e7eb",
                  background: page === i + 1 ? "#111827" : "#fff",
                  color: page === i + 1 ? "#fff" : "#374151",
                  cursor: "pointer",
                }}
              >{i + 1}</button>
            ))}
            <button disabled={page === pagination.totalPages} onClick={() => setPage((p) => p + 1)}
              style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: page === pagination.totalPages ? "not-allowed" : "pointer" }}
            >Next</button>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <>
          <div onClick={handleClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)", zIndex: 1040 }} />
          <div style={{
            position: "fixed", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1050, width: "100%", maxWidth: 520,
            background: "#fff", borderRadius: 20,
            boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
            padding: "32px 32px 28px",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
                {editItem ? "Edit Category" : "New Category"}
              </h2>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ background: "#eff6ff", color: "#6362e7", fontSize: 11, fontWeight: 700, letterSpacing: 1, padding: "4px 12px", borderRadius: 20, border: "1px solid #6362e7" }}>
                  {editItem ? "EDITING" : "ADDING"}
                </span>
                <button onClick={handleClose} style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid #e5e7eb", background: "#f9fafb", cursor: "pointer", fontSize: 16, color: "#555" }}>×</button>
              </div>
            </div>

            <hr style={{ borderColor: "#f0f0f0", marginBottom: 20 }} />

            {formError && (
              <div style={{ background: "#fef2f2", color: "#dc2626", padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
                {formError}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#888", marginBottom: 8, textTransform: "uppercase" }}>
                Category Name <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Cinnamon Powder"
                style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1.5px solid #e5e7eb", fontSize: 14, boxSizing: "border-box", outline: "none" }}
                onFocus={(e) => e.target.style.borderColor = "#6362e7"}
                onBlur={(e)  => e.target.style.borderColor = "#e5e7eb"}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#888", marginBottom: 8, textTransform: "uppercase" }}>
                Category Image
              </label>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); handleImage(e.dataTransfer.files[0]); }}
                onClick={() => document.getElementById("catImageInput").click()}
                style={{ border: `2px dashed ${dragOver ? "#4f4fd0" : "#6362e7"}`, borderRadius: 12, background: preview ? "#fff" : "#eff6ff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 120, cursor: "pointer", overflow: "hidden" }}
              >
                {preview ? (
                  <>
                    <img src={preview} alt="preview" style={{ maxHeight: 110, maxWidth: "100%", objectFit: "contain", borderRadius: 8 }} />
                    <span style={{ fontSize: 11, color: "#999", marginTop: 6 }}>Click to change</span>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 26, marginBottom: 4 }}>🖼️</div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#555" }}>UPLOAD IMAGE</span>
                    <span style={{ fontSize: 11, color: "#aaa" }}>PNG, JPG, WEBP</span>
                  </>
                )}
              </div>
              <input id="catImageInput" type="file" accept="image/png,image/jpeg,image/webp"
                onChange={(e) => handleImage(e.target.files[0])} style={{ display: "none" }} />
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={handleClose} style={{ padding: "11px 24px", borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#f9fafb", color: "#555", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} style={{ flex: 1, padding: "11px 24px", borderRadius: 10, border: "none", background: saving ? "#a5a4f3" : "#6362e7", color: "#fff", fontWeight: 700, fontSize: 14, cursor: saving ? "not-allowed" : "pointer" }}>
                {saving ? "Saving..." : editItem ? "Update Category" : "Save Category"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ListCategory;